/**
 * custom-scrollbar.js
 *
 * Generic, reusable custom scrollbar as a native Custom Element.
 * Hides itself automatically when the linked scroll area does not overflow.
 * Supports draggable thumb. Thumb width reflects the visible/total ratio.
 *
 * Usage:
 *   <div id="my-scroll-area">...</div>
 *   <custom-scrollbar aria-controls="my-scroll-area">
 *     <div class="js-scroll-track">
 *       <div class="js-scroll-thumb"></div>
 *     </div>
 *   </custom-scrollbar>
 *
 * CSS hooks (set on the host element, inherit to children):
 *   --scrollbar-thumb-width   percentage string, e.g. "40%"
 *   --scrollbar-thumb-offset  px offset, e.g. "64px"
 */

function throttle(fn, limit) {
  let lastCall = 0;
  return function (...args) {
    const now = Date.now();
    if (now - lastCall >= limit) {
      lastCall = now;
      fn.apply(this, args);
    }
  };
}

class CustomScrollbar extends HTMLElement {
  connectedCallback() {
    const controlsId = this.getAttribute('aria-controls');
    if (!controlsId) {
      console.warn('[custom-scrollbar] Missing aria-controls attribute.');
      return;
    }

    this._scrollArea = document.getElementById(controlsId);
    if (!this._scrollArea) {
      console.warn(`[custom-scrollbar] No element with id "${controlsId}".`);
      return;
    }

    this._track = this.querySelector('.js-scroll-track');
    this._thumb = this.querySelector('.js-scroll-thumb');

    if (!this._thumb) {
      console.warn('[custom-scrollbar] No .js-scroll-thumb found.');
      return;
    }

    // Bind callbacks once so the same reference can be removed
    this._onScroll = throttle(this._syncThumb.bind(this), 10);
    this._onMouseDown = this._handleMouseDown.bind(this);
    this._onMouseMove = this._handleMouseMove.bind(this);
    this._onMouseUp = this._handleMouseUp.bind(this);
    this._onResize = throttle(this._checkOverflow.bind(this), 100);

    this._checkOverflow();

    this._resizeObserver = new ResizeObserver(this._onResize);
    this._resizeObserver.observe(this._scrollArea);

    this._scrollArea.addEventListener('scroll', this._onScroll, { passive: true });
    this._thumb.addEventListener('mousedown', this._onMouseDown);
  }

  disconnectedCallback() {
    this._scrollArea?.removeEventListener('scroll', this._onScroll);
    this._thumb?.removeEventListener('mousedown', this._onMouseDown);
    this._resizeObserver?.disconnect();
  }

  _checkOverflow() {
    if (!this._scrollArea) return;
    const hasOverflow = this._scrollArea.scrollWidth > this._scrollArea.clientWidth;
    this.classList.toggle('is-hidden', !hasOverflow);
    this._scrollArea.classList.toggle('is-scrollable', hasOverflow);
    if (hasOverflow) this._syncThumb();
  }

  _syncThumb() {
    const { scrollLeft, scrollWidth, clientWidth } = this._scrollArea;

    // Size thumb proportionally to what fraction of the content is visible
    const visibleRatio = clientWidth / scrollWidth;
    this.style.setProperty('--scrollbar-thumb-width', `${visibleRatio * 100}%`);

    // Position thumb within the track
    const trackWidth = (this._track ?? this).clientWidth;
    const thumbWidth = this._thumb.offsetWidth;
    const thumbTravel = trackWidth - thumbWidth;
    const scrollable = scrollWidth - clientWidth;
    const progress = scrollable > 0 ? scrollLeft / scrollable : 0;
    this.style.setProperty('--scrollbar-thumb-offset', `${thumbTravel * progress}px`);
  }

  _handleMouseDown(e) {
    e.preventDefault();
    this._isDragging = true;
    this._dragStartX = e.clientX;
    this._scrollStartX = this._scrollArea.scrollLeft;
    document.addEventListener('mousemove', this._onMouseMove);
    document.addEventListener('mouseup', this._onMouseUp);
    this._thumb.classList.add('is-dragging');
    this._scrollArea.classList.add('is-dragging');
  }

  _handleMouseMove(e) {
    if (!this._isDragging) return;
    const deltaX = e.clientX - this._dragStartX;
    const trackWidth = (this._track ?? this).clientWidth;
    const thumbTravel = trackWidth - this._thumb.offsetWidth;
    if (thumbTravel <= 0) return;
    const scrollRatio = (this._scrollArea.scrollWidth - this._scrollArea.clientWidth) / thumbTravel;
    this._scrollArea.scrollLeft = this._scrollStartX + deltaX * scrollRatio;
  }

  _handleMouseUp() {
    this._isDragging = false;
    document.removeEventListener('mousemove', this._onMouseMove);
    document.removeEventListener('mouseup', this._onMouseUp);
    this._thumb.classList.remove('is-dragging');
    this._scrollArea.classList.remove('is-dragging');
  }
}

if (!customElements.get('custom-scrollbar')) {
  customElements.define('custom-scrollbar', CustomScrollbar);
}
